/**
 * ChangeStatusModal - quick status transition for a single goal node
 * (status select + optional note, recorded on the goal's status history).
 */

import { useEffect, useState } from 'react';
import styled from 'styled-components';
import type { GoalNode, GoalStatus } from '@upskill/shared';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui';
import { useUpdateGoal } from '../../hooks/useGoals';
import { extractApiError } from '../../utils/apiError';
import { STATUS_BADGE_VARIANT, STATUS_LABELS, STATUS_OPTIONS } from './statusMeta';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const CurrentRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Textarea = styled.textarea`
  min-height: 80px;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }
`;

const FormError = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.error.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

interface ChangeStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  goal: GoalNode | null;
  /** Overlay z-index — raise this when opened on top of another high-z overlay (e.g. a drawer). */
  zIndex?: number;
}

export function ChangeStatusModal({
  isOpen,
  onClose,
  clientId,
  goal,
  zIndex,
}: ChangeStatusModalProps) {
  const updateGoal = useUpdateGoal(clientId);
  const [status, setStatus] = useState<GoalStatus>('ACTIVE');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (isOpen && goal) {
      setStatus(goal.status);
      setNote('');
      updateGoal.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, goal]);

  const handleClose = () => {
    updateGoal.reset();
    onClose();
  };

  const handleConfirm = () => {
    if (!goal) return;
    updateGoal.mutate(
      { goalId: goal.id, dto: { status, note: note.trim() || undefined } },
      { onSuccess: handleClose },
    );
  };

  if (!goal) return null;

  const errorMessage = updateGoal.isError
    ? extractApiError(updateGoal.error, 'Failed to update status. Please try again.')
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Change Status"
      size="sm"
      zIndex={zIndex}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={updateGoal.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary-blue"
            onClick={handleConfirm}
            isLoading={updateGoal.isPending}
            disabled={status === goal.status}
          >
            Update Status
          </Button>
        </>
      }
    >
      <Body>
        {errorMessage && <FormError>{errorMessage}</FormError>}

        <CurrentRow>
          <strong>{goal.name}</strong> is currently
          <Badge variant={STATUS_BADGE_VARIANT[goal.status]}>{STATUS_LABELS[goal.status]}</Badge>
        </CurrentRow>

        <Select
          label="New Status"
          options={STATUS_OPTIONS}
          value={status}
          onChange={(e) => setStatus(e.target.value as GoalStatus)}
          fullWidth
        />

        <FieldGroup>
          <FieldLabel htmlFor="status-change-note">Note (optional)</FieldLabel>
          <Textarea
            id="status-change-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reason for the status change..."
          />
        </FieldGroup>
      </Body>
    </Modal>
  );
}

export default ChangeStatusModal;
